import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { trpc } from "../lib/trpc";
import "./Login.css";

export function LoginPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [remember, setRemember] = useState(false);
  const [lang, setLang] = useState<"TR" | "EN">("TR");

  const utils = trpc.useUtils();
  const loginMutation = trpc.auth.login.useMutation({
    onSuccess: async () => {
      // Giriş sonrası auth.me'yi yeni oturum çereziyle HEMEN tazele — yoksa ProtectedRoute guard'ı
      // login öncesi cache'lenmiş "oturum yok" sonucunu görüp tekrar /login'e atıyordu (hard refresh gerekliliği).
      await utils.auth.me.refetch();
      navigate("/dashboard", { replace: true });
    },
  });

  const submit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    loginMutation.reset();
    loginMutation.mutate({ email, password });
  };

  // tRPC hatası → kullanıcı-dostu mesaj.
  // Zod validation hatası ise alan-bazlı bullet listesi, yoksa server mesajı.
  const errorMessage = (() => {
    const err = loginMutation.error;
    if (!err) return null;
    const zodFieldErrors = err.data?.zodError?.fieldErrors as
      | Record<string, string[] | undefined>
      | undefined;
    if (zodFieldErrors) {
      const msgs = Object.values(zodFieldErrors).flat().filter(Boolean) as string[];
      if (msgs.length) return msgs.join(" · ");
    }
    return err.message || "Bilinmeyen bir hata oluştu.";
  })();

  return (
    <>
      <a href="#login-form" className="login-skip">
        Forma geç
      </a>

      <div className="login-shell">
        {/* ─── BRAND (left) ─── */}
        <aside className="login-brand" aria-label="STEPERP marka tanıtım paneli">
          <a href="/" className="login-brand-logo" aria-label="STEPERP ana sayfası">
            <span className="mark" aria-hidden="true">
              <svg
                width="22"
                height="22"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={2.2}
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <rect x="3" y="14" width="5" height="7" rx="1" fill="currentColor" stroke="none" />
                <rect
                  x="9.5"
                  y="9"
                  width="5"
                  height="12"
                  rx="1"
                  fill="currentColor"
                  stroke="none"
                  opacity="0.7"
                />
                <rect
                  x="16"
                  y="4"
                  width="5"
                  height="17"
                  rx="1"
                  fill="currentColor"
                  stroke="none"
                  opacity="0.4"
                />
              </svg>
            </span>
            <span className="word">
              <span className="name">STEPERP</span>
              <span className="sub">Endüstriyel Platform</span>
            </span>
          </a>

          <div className="login-brand-hero">
            <div className="eyebrow">Uçtan uca izlenebilirlik</div>
            <h1>
              Bir gram un bile <em>gözden kaçmaz.</em>
            </h1>
            <p>
              STEPERP — tedarikten sevkiyata kadar her hammaddeyi, her reçeteyi, her tartımı gerçek
              zamanlı takip eden gıda üretim kontrol panelidir.
            </p>
          </div>

          <ul className="login-feat" aria-label="Öne çıkan özellikler">
            <li className="login-feat-item">
              <span className="ic-box" aria-hidden="true">
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={1.75}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="m12 3 9 5v8l-9 5-9-5V8z" />
                  <path d="m3 8 9 5 9-5" />
                  <path d="M12 13v8" />
                </svg>
              </span>
              <div>
                <div className="t">Lot bazlı izlenebilirlik</div>
                <div className="s">Her parti otomatik lot kodu · FEFO tüketim · recall hazır</div>
              </div>
            </li>
            <li className="login-feat-item">
              <span className="ic-box" aria-hidden="true">
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={1.75}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M12 3v18" />
                  <path d="M6 21h12" />
                  <path d="m6 8 12-3" />
                  <path d="M3 14a3 3 0 0 0 6 0L6 7z" />
                  <path d="M15 13a3 3 0 0 0 6 0l-3-7z" />
                </svg>
              </span>
              <div>
                <div className="t">Kütle korunumu doğrulama</div>
                <div className="s">Her tartımda anlık fire kontrolü · verim varyans raporu</div>
              </div>
            </li>
            <li className="login-feat-item">
              <span className="ic-box" aria-hidden="true">
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={1.75}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M3 3v18h18" />
                  <path d="M7 14v4" />
                  <path d="M12 8v10" />
                  <path d="M17 4v14" />
                </svg>
              </span>
              <div>
                <div className="t">Çok seviyeli reçete</div>
                <div className="s">Premiks → hamur → mamul · recursive BOM patlatma</div>
              </div>
            </li>
            <li className="login-feat-item">
              <span className="ic-box" aria-hidden="true">
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={1.75}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <rect x="3" y="11" width="18" height="11" rx="2" />
                  <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                </svg>
              </span>
              <div>
                <div className="t">Kurumsal güvenlik</div>
                <div className="s">SSO · 2FA · rol bazlı yetki · audit log · ISO 27001 uyumu</div>
              </div>
            </li>
          </ul>

          <div className="login-brand-foot">
            <div className="left">
              <div className="stat">
                <span className="v">14</span>
                <span className="l">Aktif tesis</span>
              </div>
              <div className="div" aria-hidden="true" />
              <div className="stat">
                <span className="v">
                  847,3<span className="u">ton</span>
                </span>
                <span className="l">Günlük takip</span>
              </div>
              <div className="div" aria-hidden="true" />
              <div className="stat">
                <span className="v">
                  99,98<span className="u">%</span>
                </span>
                <span className="l">Uptime</span>
              </div>
            </div>
            <div>
              <span className="login-status-pill">Tüm sistemler çalışıyor</span>
            </div>
          </div>
        </aside>

        {/* ─── FORM (right) ─── */}
        <main className="login-form-panel" id="login-form">
          <div className="login-fp-top">
            <div className="login-lang" role="tablist" aria-label="Dil seçimi">
              <button
                type="button"
                role="tab"
                aria-selected={lang === "TR"}
                className={lang === "TR" ? "active" : ""}
                onClick={() => setLang("TR")}
              >
                TR
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={lang === "EN"}
                className={lang === "EN" ? "active" : ""}
                onClick={() => setLang("EN")}
              >
                EN
              </button>
            </div>
            <a href="#help" className="login-help">
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={1.75}
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <circle cx="12" cy="12" r="9" />
                <path d="M9.1 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
                <path d="M12 17h.01" />
              </svg>
              Yardım merkezi
            </a>
          </div>

          <div className="login-form-wrap">
            <header className="login-form-head">
              <h1 className="title">Tekrar hoş geldiniz</h1>
              <p className="sub">Devam etmek için STEPERP hesabınıza giriş yapın.</p>
            </header>

            <button
              type="button"
              className="login-sso"
              aria-label="Microsoft hesabı ile tek tıklama giriş"
            >
              {/* Microsoft resmi logo renkleri — marka geregi sabit, token muaf (tek mesru istisna) */}
              <svg width="18" height="18" viewBox="0 0 21 21" aria-hidden="true">
                <rect x="1" y="1" width="9" height="9" fill="#F25022" />
                <rect x="11" y="1" width="9" height="9" fill="#7FBA00" />
                <rect x="1" y="11" width="9" height="9" fill="#00A4EF" />
                <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
              </svg>
              <span>Microsoft ile devam et</span>
              <span className="badge">SSO</span>
            </button>

            <div className="login-divider">veya kurumsal kimlik bilgileriniz</div>

            {errorMessage ? (
              <div className="login-err" role="alert" aria-live="polite">
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={2}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <circle cx="12" cy="12" r="9" />
                  <path d="M12 8v4" />
                  <path d="M12 16h.01" />
                </svg>
                <div>
                  <b>Giriş başarısız.</b> {errorMessage}
                </div>
              </div>
            ) : null}

            <form onSubmit={submit} autoComplete="on" noValidate>
              <div className="login-field">
                <label htmlFor="email" className="login-label">
                  İş e-postası <span className="req" aria-hidden="true">*</span>
                </label>
                <div className="login-input-wrap">
                  <input
                    id="email"
                    name="email"
                    type="email"
                    className="login-input"
                    inputMode="email"
                    autoComplete="username"
                    autoCapitalize="off"
                    spellCheck={false}
                    required
                    placeholder="ad.soyad@steperp.com"
                    aria-required="true"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                  <span className="ic-lead" aria-hidden="true">
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth={1.75}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <rect x="3" y="5" width="18" height="14" rx="2" />
                      <path d="m3 7 9 6 9-6" />
                    </svg>
                  </span>
                </div>
              </div>

              <div className="login-field">
                <div className="login-field-row">
                  <label htmlFor="password" className="login-label">
                    Parola <span className="req" aria-hidden="true">*</span>
                  </label>
                  <a href="#forgot" className="login-link">
                    Parolamı unuttum
                  </a>
                </div>
                <div className="login-input-wrap">
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? "text" : "password"}
                    className="login-input has-toggle"
                    autoComplete="current-password"
                    required
                    placeholder="En az 8 karakter"
                    aria-required="true"
                    minLength={8}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                  <span className="ic-lead" aria-hidden="true">
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth={1.75}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <rect x="4" y="11" width="16" height="10" rx="2" />
                      <path d="M8 11V7a4 4 0 0 1 8 0v4" />
                    </svg>
                  </span>
                  <button
                    type="button"
                    className="login-toggle-pwd"
                    aria-label={showPassword ? "Parolayı gizle" : "Parolayı göster"}
                    aria-pressed={showPassword}
                    aria-controls="password"
                    onClick={() => setShowPassword((v) => !v)}
                  >
                    {showPassword ? (
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth={1.75}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                      >
                        <path d="m3 3 18 18" />
                        <path d="M10.6 5.1A9 9 0 0 1 12 5c7 0 11 7 11 7a18 18 0 0 1-3.2 4" />
                        <path d="M6.6 6.6A18 18 0 0 0 1 12s4 7 11 7a9 9 0 0 0 5.4-1.6" />
                        <path d="M14.1 14.1a3 3 0 0 1-4.2-4.2" />
                      </svg>
                    ) : (
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth={1.75}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                      >
                        <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7" />
                        <circle cx="12" cy="12" r="3" />
                      </svg>
                    )}
                  </button>
                </div>
              </div>

              <div className="login-options">
                <label className="login-chk" htmlFor="remember">
                  <input
                    type="checkbox"
                    id="remember"
                    name="remember"
                    checked={remember}
                    onChange={(e) => setRemember(e.target.checked)}
                  />
                  <span>Bu cihazda beni hatırla</span>
                </label>
                <span className="mono" style={{ fontSize: "11.5px", color: "var(--neutral-400)" }}>
                  7 gün
                </span>
              </div>

              <button
                type="submit"
                className={`login-cta ${loginMutation.isPending ? "loading" : ""}`}
                disabled={loginMutation.isPending}
              >
                <span className="spinner" aria-hidden="true" />
                <span className="label">Oturum Aç</span>
              </button>

              <div className="login-form-foot">
                Hesabınız yok mu? Kurumsal erişim için{" "}
                <a href="#contact">sistem yöneticinize başvurun</a>.
              </div>
            </form>
          </div>

          <div className="login-fp-bot">
            <div className="version">STEPERP v0.1.0 · Faz 0</div>
            <div className="links">
              <a href="#terms">Kullanım Şartları</a>
              <a href="#privacy">Gizlilik</a>
              <a href="#status">Sistem Durumu</a>
            </div>
          </div>
        </main>
      </div>
    </>
  );
}
